// Phase 13 — Shopify-style native Lifted Inventory Editor
// =======================================================
// Self-contained editor for any product in /api/admin/inventory/products.
// No Shopify sync buttons. No UpPromote dependency. Stripe = payment.
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Save, Copy, Eye, MoreHorizontal, Trash2, ImagePlus,
  ImageIcon, X, GripVertical, Loader2, Plus, ShieldCheck,
  AlertTriangle, ChevronUp, ChevronDown, Truck, Package,
  Tag as TagIcon, FileText, Search, RefreshCw, Check, AlertCircle,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const STATUSES = ["active", "draft", "archived"];

function money(c) { if (c == null) return ""; return (c/100).toFixed(2); }

export default function AdminInventoryEditor() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const isNew = !handle;

  const [p, setP] = useState(isNew ? blankProduct() : null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [squareBusy, setSquareBusy] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (isNew) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.get(`/admin/inventory/products/${handle}`);
      setP({ ...blankProduct(), ...data.product });
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  }, [handle, isNew]);
  useEffect(() => { load(); }, [load]);

  // Patch helpers ----------------------------------------------------------
  const update = (patch) => { setP((prev) => ({ ...prev, ...patch })); setDirty(true); };

  const save = async () => {
    setSaving(true); setErr(""); setOk("");
    // Phase 22+ — Required taxonomy validation
    if (!p.title || !p.title.trim()) {
      setErr("Title is required."); setSaving(false); return;
    }
    if (!p.owner_type) {
      setErr("Owner type is required (Lifted / Partner / Franchise)."); setSaving(false); return;
    }
    if (!p.category) {
      setErr("Category is required (Lifted Gear / Lifted Accessories / Partner Products)."); setSaving(false); return;
    }
    if (p.category === "lifted-gear" && !p.product_type) {
      setErr("Product type is required for Lifted Gear (T-Shirt / Shorts / Hat / Hoodie / Other)."); setSaving(false); return;
    }
    try {
      if (isNew) {
        // Phase 22 — pre-save image uploads. `p.images` may contain entries
        // with `_pending: true` carrying base64 data. The backend create
        // endpoint normalizes both URL strings and {base64,...} dicts.
        const imagesPayload = (p.images || []).map((img) => {
          if (img && img._pending && img._base64) {
            return {
              base64: img._base64,
              filename: img._filename || "image.png",
              content_type: img._content_type || "image/png",
              alt_text: img.alt_text || p.title || "Product image",
            };
          }
          return img && (img.src || img.url) ? (img.src || img.url) : null;
        }).filter(Boolean);
        // primary image index — use whatever is at position 0 in our list
        // (the user can reorder before save). If a `primary_image_id` is set,
        // resolve its index instead.
        let primaryIdx = 0;
        if (p.primary_image_id) {
          primaryIdx = (p.images || []).findIndex((i) => i.id === p.primary_image_id);
          if (primaryIdx < 0) primaryIdx = 0;
        }
        const { data } = await api.post("/admin/inventory/products", {
          ...p,
          images: imagesPayload,
          primary_image_index: primaryIdx,
        });
        setOk("Created."); setDirty(false);
        navigate(`/admin/inventory/${encodeURIComponent(data.product.handle)}`, { replace: true });
        return;
      }
      const patchBody = {
        title: p.title, description: p.description, vendor: p.vendor,
        product_type: p.product_type, tags: p.tags, metafields: p.metafields,
        seo_title: p.seo_title, seo_description: p.seo_description,
        collections: p.collections, category: p.category,
        owner_type: p.owner_type,
        featured: !!p.featured, is_new: !!p.is_new, is_top_product: !!p.is_top_product,
      };
      await Promise.all([
        api.patch(`/admin/inventory/products/${handle}`, patchBody),
        api.patch(`/admin/inventory/products/${handle}/price`, {
          price_cents: Number(p.price_min_cents ?? p.price_cents) || null,
          compare_at_price_cents: Number(p.compare_at_price_cents) || null,
        }),
        api.patch(`/admin/inventory/products/${handle}/stock`, {
          inventory_count: Number(p.inventory_count) || 0,
        }),
        api.patch(`/admin/inventory/products/${handle}/availability`, {
          shipping_available: !!p.shipping_available,
          same_day_available: !!p.same_day_eligible,
        }),
        api.patch(`/admin/inventory/products/${handle}/status`, { status: p.status || "draft" }),
      ]);
      setOk("Saved.");
      setDirty(false);
      load();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = String(reader.result || "");
      const b64 = dataUrl.split(",")[1];
      // Phase 22 — when the product hasn't been saved yet, stage the upload
      // locally so the user can select multiple images and pick a primary
      // BEFORE the first save. On save we ship the base64 along with the
      // create payload.
      if (isNew) {
        const tmpId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const pendingImg = {
          id: tmpId,
          src: dataUrl,
          alt_text: p.title || "Product image",
          _pending: true,
          _base64: b64,
          _filename: file.name,
          _content_type: file.type,
        };
        setP((prev) => ({ ...prev, images: [...(prev.images || []), pendingImg] }));
        setDirty(true);
        setOk("Image staged. It will upload when you save the product.");
        return;
      }
      try {
        const { data } = await api.post(`/admin/inventory/products/${handle}/image`, {
          base64: b64, filename: file.name, content_type: file.type,
          alt_text: p.title || "Product image",
        });
        setP((prev) => ({ ...prev, images: data.images }));
        setOk("Image added.");
      } catch (e) { setErr(apiErrorMessage(e)); }
    };
    reader.readAsDataURL(file);
  };

  const removeImage = async (imageId) => {
    // Phase 22 — pending (pre-save) images live only in local state; just
    // drop them. Saved images go through the API.
    const img = (p.images || []).find((i) => i.id === imageId);
    if (!img) return;
    if (img._pending || isNew) {
      setP((prev) => ({ ...prev, images: (prev.images || []).filter((i) => i.id !== imageId) }));
      setDirty(true);
      return;
    }
    try {
      const { data } = await api.delete(`/admin/inventory/products/${handle}/image/${imageId}`);
      setP((prev) => ({ ...prev, images: data.images }));
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  const moveImage = async (imageId, direction) => {
    const imgs = (p.images || []).slice();
    const i = imgs.findIndex((x) => x.id === imageId);
    const j = i + direction;
    if (i < 0 || j < 0 || j >= imgs.length) return;
    const tmp = imgs[i]; imgs[i] = imgs[j]; imgs[j] = tmp;
    // Phase 22 — if any pending images exist OR we're in new mode, reorder
    // locally only (the backend has nothing to update yet).
    if (isNew || imgs.some((x) => x._pending)) {
      setP((prev) => ({ ...prev, images: imgs }));
      setDirty(true);
      return;
    }
    try {
      await api.patch(`/admin/inventory/products/${handle}/images/reorder`,
                       { order: imgs.map((x) => x.id) });
      setP((prev) => ({ ...prev, images: imgs }));
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  // Phase 22 — explicit "Set as primary" action. Moves the chosen image to
  // index 0 (the storefront uses media[0] as the canonical primary).
  const setPrimaryImage = async (imageId) => {
    const imgs = (p.images || []).slice();
    const idx = imgs.findIndex((x) => x.id === imageId);
    if (idx <= 0) return; // already primary or not found
    const [chosen] = imgs.splice(idx, 1);
    imgs.unshift(chosen);
    if (isNew || imgs.some((x) => x._pending)) {
      setP((prev) => ({ ...prev, images: imgs, primary_image_id: imageId }));
      setDirty(true);
      return;
    }
    try {
      const { data } = await api.post(
        `/admin/inventory/products/${handle}/images/${imageId}/primary`,
      );
      setP((prev) => ({ ...prev, images: data.images }));
      setOk("Primary image updated.");
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  const duplicate = async () => {
    if (isNew) return;
    try {
      const { data } = await api.post(`/admin/inventory/products/${handle}/duplicate`);
      navigate(`/admin/inventory/${encodeURIComponent(data.product.handle)}`);
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  const runCompliance = async () => {
    if (isNew) return;
    try {
      await api.post(`/admin/inventory/products/${handle}/run-compliance`);
      setOk("Compliance check queued.");
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  // Iter85 — Per-product manual Square refresh. Calls the existing
  // admin-only single-handle sync endpoint and surfaces success/error.
  // Pre-save (dirty) products are auto-saved first so Square gets the
  // latest title/price/image.
  const refreshSquare = async () => {
    if (isNew) return;
    setSquareBusy(true); setErr(""); setOk("");
    try {
      if (dirty) {
        await save();
      }
      const { data } = await api.post(
        `/admin/inventory/products/${encodeURIComponent(handle)}/square-refresh`,
      );
      setOk(`Square sync ${data.status || "ok"}.`);
      // Refresh the product to pick up the new square_synced_at / IDs.
      await load();
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setSquareBusy(false);
    }
  };

  if (busy && !p) return <div className="p-10 text-center text-lifted-dim text-sm">Loading…</div>;
  if (!p) return <div className="p-10 text-center text-lifted-dim text-sm">Product not found.</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-5" data-testid="admin-inventory-editor">
      {/* TOP HEADER */}
      <header className="flex items-center justify-between gap-2 mb-4 sticky top-0 z-10 bg-black/85 backdrop-blur py-3 -mx-4 px-4 border-b border-white/5">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/admin/inventory" className="text-lifted-dim hover:text-white" data-testid="inv-back">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <input
            value={p.title || ""}
            onChange={(e) => update({ title: e.target.value })}
            placeholder="Product title"
            className="bg-transparent font-anton text-xl uppercase text-white border-none focus:outline-none focus:ring-0 px-0 truncate"
            data-testid="inv-title"
          />
          <span className={`px-2 py-0.5 rounded border text-[0.6rem] font-bebas tracking-[0.22em] uppercase ${
            p.status === "active"   ? "bg-lifted-green/15 text-lifted-green border-lifted-green/40"
            : p.status === "archived" ? "bg-white/10 text-white/50 border-white/20"
            : "bg-amber-400/15 text-amber-300 border-amber-400/40"
          }`} data-testid="inv-status-badge">
            {p.status || "draft"}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {!isNew && (
            <>
              <button onClick={duplicate} title="Duplicate" className="btn-ghost text-[0.65rem] inline-flex items-center gap-1" data-testid="inv-duplicate">
                <Copy className="h-3 w-3" /> Duplicate
              </button>
              <a href={`/products/${p.handle}`} target="_blank" rel="noreferrer"
                 className="btn-ghost text-[0.65rem] inline-flex items-center gap-1" data-testid="inv-view">
                <Eye className="h-3 w-3" /> View
              </a>
            </>
          )}
          <button onClick={save} disabled={!dirty || saving}
                  className="btn-lifted text-xs inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="inv-save">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
          </button>
        </div>
      </header>

      {ok && <div className="text-lifted-green text-xs mb-2" data-testid="inv-ok">{ok}</div>}
      {err && <div className="text-rose-300 text-xs mb-2" data-testid="inv-err">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* LEFT COLUMN */}
        <div className="space-y-4">

          {/* 1. Product info */}
          <Card title="Product information" testid="inv-card-info">
            <Label>Title</Label>
            <input value={p.title || ""} onChange={(e) => update({ title: e.target.value })}
                   className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-lifted-green/40"
                   data-testid="inv-info-title" />
            <Label>Description</Label>
            <textarea value={p.description || ""} onChange={(e) => update({ description: e.target.value })}
                      rows={8}
                      className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-lifted-green/40 font-mono"
                      data-testid="inv-info-description"
                      placeholder="Specs, care, size chart, paragraphs…" />
            <p className="text-[0.65rem] text-lifted-dim mt-1">
              Supports product specs, care instructions, size charts. Plain markdown/HTML.
            </p>
          </Card>

          {/* 2. Media */}
          <Card title="Media" testid="inv-card-media">
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {(p.images || []).map((img, idx) => {
                const isPrimary = idx === 0;
                return (
                <div key={img.id || img.src} className={`relative group aspect-square bg-black/40 rounded overflow-hidden border ${isPrimary ? "border-lifted-green" : "border-white/10"}`}
                     data-testid={`inv-image-${img.id}`}>
                  <img src={img.src || img.url} alt={img.alt_text || `image-${idx}`}
                       className="h-full w-full object-cover" />
                  {/* Phase 22 — primary indicator */}
                  {isPrimary && (
                    <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-lifted-green text-black text-[8px] font-bebas tracking-[0.18em] uppercase"
                         data-testid={`inv-image-${img.id}-primary-badge`}>
                      Primary
                    </div>
                  )}
                  {/* Phase 22 — staged-upload indicator */}
                  {img._pending && (
                    <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-amber-400/90 text-black text-[8px] font-bebas tracking-[0.18em] uppercase"
                         data-testid={`inv-image-${img.id}-pending-badge`}>
                      Staged
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5">
                    {!isPrimary && (
                      <button onClick={() => setPrimaryImage(img.id)} className="text-lifted-green hover:text-white text-[0.6rem] font-bebas tracking-[0.18em] uppercase" title="Make primary" data-testid={`inv-image-${img.id}-make-primary`}>
                        Make Primary
                      </button>
                    )}
                    <div className="flex items-center gap-2">
                      <button onClick={() => moveImage(img.id, -1)} className="text-white hover:text-lifted-green" title="Move up" data-testid={`inv-image-${img.id}-up`}>
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button onClick={() => moveImage(img.id, 1)} className="text-white hover:text-lifted-green" title="Move down" data-testid={`inv-image-${img.id}-down`}>
                        <ChevronDown className="h-4 w-4" />
                      </button>
                      <button onClick={() => removeImage(img.id)} className="text-rose-300 hover:text-rose-200" title="Remove" data-testid={`inv-image-${img.id}-remove`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
              })}
              {/* Phase 22 — upload tile is now ALWAYS available, including
                  for unsaved (new) products. Files are staged locally and
                  shipped with the create payload on save. */}
              <label className="aspect-square bg-black/30 border border-dashed border-white/15 rounded grid place-items-center cursor-pointer hover:border-lifted-green/40 hover:bg-lifted-green/5"
                     data-testid="inv-image-add">
                <input ref={fileRef} type="file" accept="image/*" className="hidden"
                       onChange={(e) => uploadImage(e.target.files?.[0])} />
                <ImagePlus className="h-6 w-6 text-lifted-dim" />
              </label>
            </div>
            <p className="text-[0.65rem] text-lifted-dim mt-2">
              {isNew
                ? "Add images now — they will upload when you save the product. The first image is the primary; use \"Make Primary\" to change it."
                : "First image is the primary. Use \"Make Primary\" on any other image to swap."}
            </p>
          </Card>

          {/* 3. Category */}
          <Card title="Category" testid="inv-card-category">
            <Label>Product category</Label>
            <select value={p.category || ""} onChange={(e) => update({ category: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="inv-category">
              <option value="">Uncategorized</option>
              <option value="apparel">Apparel · Tops · T-Shirts</option>
              <option value="apparel-bottoms">Apparel · Bottoms</option>
              <option value="apparel-headwear">Apparel · Headwear</option>
              <option value="accessories">Accessories</option>
              <option value="lifestyle">Lifestyle</option>
              <option value="supplies">Lifted Supplies</option>
              <option value="partner-goods">Partner Goods</option>
            </select>
            <p className="text-[0.65rem] text-lifted-dim mt-1">Affects search, filtering, and metafields.</p>
          </Card>

          {/* 4. Variants */}
          <VariantsCard p={p} update={update} handle={handle} isNew={isNew} setOk={setOk} setErr={setErr} />

          {/* 5. Purchase options */}
          <Card title="Purchase options" testid="inv-card-options">
            <Toggle label="Same-day delivery (Lifted Supplies)"
                    checked={!!p.same_day_eligible}
                    onChange={(v) => update({ same_day_eligible: v })}
                    testid="inv-opt-sameday" />
            <Toggle label="Mail shipping" checked={p.shipping_available !== false}
                    onChange={(v) => update({ shipping_available: v })}
                    testid="inv-opt-shipping" />
            <p className="text-[0.65rem] text-lifted-dim mt-2">
              Connected to Lifted Supplies + Stripe checkout. No Shopify dependency.
            </p>
          </Card>

          {/* 6. Metafields */}
          <Card title="Details" testid="inv-card-details">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Item # / SKU" value={p.metafields?.sku} onChange={(v) => update({ metafields: { ...p.metafields, sku: v }})} testid="inv-meta-sku" />
              <Field label="Gender" value={p.metafields?.gender} onChange={(v) => update({ metafields: { ...p.metafields, gender: v }})} testid="inv-meta-gender" />
              <Field label="Fabric" value={p.metafields?.fabric} onChange={(v) => update({ metafields: { ...p.metafields, fabric: v }})} testid="inv-meta-fabric" />
              <Field label="Weight (g)" value={p.metafields?.weight_g} onChange={(v) => update({ metafields: { ...p.metafields, weight_g: v }})} testid="inv-meta-weight" />
              <Field label="Print size" value={p.metafields?.print_size} onChange={(v) => update({ metafields: { ...p.metafields, print_size: v }})} testid="inv-meta-print" />
              <Field label="Care instructions" value={p.metafields?.care} onChange={(v) => update({ metafields: { ...p.metafields, care: v }})} testid="inv-meta-care" />
            </div>
            <Label className="mt-3">Tags</Label>
            <input value={(p.tags || []).join(", ")}
                   onChange={(e) => update({ tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
                   placeholder="comma, separated, tags"
                   className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm"
                   data-testid="inv-tags" />
          </Card>

          {/* 7. SEO */}
          <Card title="Search engine listing" testid="inv-card-seo">
            <div className="rounded border border-white/5 bg-black/40 p-3 mb-3">
              <div className="text-xs text-lifted-green truncate">https://liftedatthemoment.com/products/{p.handle || "[handle]"}</div>
              <div className="text-sm text-white truncate">{p.seo_title || p.title || "Product title"}</div>
              <div className="text-[0.7rem] text-lifted-dim line-clamp-2">{p.seo_description || (p.description || "").slice(0, 160)}</div>
            </div>
            <Field label="SEO title" value={p.seo_title} onChange={(v) => update({ seo_title: v })} testid="inv-seo-title" />
            <Field label="SEO description" value={p.seo_description} onChange={(v) => update({ seo_description: v })} testid="inv-seo-desc" textarea />
          </Card>
        </div>

        {/* RIGHT SIDEBAR */}
        <aside className="space-y-4">
          <Card title="Status" testid="inv-side-status">
            <select value={p.status || "draft"} onChange={(e) => update({ status: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm capitalize"
                    data-testid="inv-side-status-select">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Card>

          {/* Phase 22+ — required taxonomy. Owner type controls commission
              rate (lifted=tier, partner=3%, franchise=3%). Category drives
              where the product appears on storefront. Product type powers
              the Lifted Gear sub-filters. */}
          <Card title="Classification" testid="inv-side-classification">
            <Label>Owner type *</Label>
            <select value={p.owner_type || "lifted"}
                    onChange={(e) => update({ owner_type: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm"
                    data-testid="inv-owner-type">
              <option value="lifted">Lifted (LATM-owned)</option>
              <option value="partner">Partner</option>
              <option value="franchise">Franchise</option>
            </select>
            <Label>Category *</Label>
            <select value={p.category || "lifted-gear"}
                    onChange={(e) => {
                      const cat = e.target.value;
                      // If switching away from Lifted Gear, clear product_type
                      update({ category: cat, ...(cat !== "lifted-gear" ? { product_type: "" } : {}) });
                    }}
                    className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm"
                    data-testid="inv-category">
              <option value="lifted-gear">Lifted Gear</option>
              <option value="lifted-accessories">Lifted Accessories</option>
              <option value="partner-products">Partner Products</option>
            </select>
            {p.category === "lifted-gear" && (
              <>
                <Label>Product type *</Label>
                <select value={p.product_type || ""}
                        onChange={(e) => update({ product_type: e.target.value })}
                        className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm"
                        data-testid="inv-product-type">
                  <option value="">— Select —</option>
                  <option value="t_shirt">T-Shirt</option>
                  <option value="shorts">Shorts</option>
                  <option value="hat">Hat</option>
                  <option value="hoodie">Hoodie</option>
                  <option value="other">Other</option>
                </select>
              </>
            )}
          </Card>

          {/* Phase 22+ — homepage / discovery flags */}
          <Card title="Discovery" testid="inv-side-discovery">
            <Toggle label="Featured product"
                    checked={!!p.featured}
                    onChange={(v) => update({ featured: v })}
                    testid="inv-flag-featured" />
            <Toggle label="Show as New"
                    checked={!!p.is_new}
                    onChange={(v) => update({ is_new: v })}
                    testid="inv-flag-new" />
            <Toggle label="Top Product"
                    checked={!!p.is_top_product}
                    onChange={(v) => update({ is_top_product: v })}
                    testid="inv-flag-top" />
          </Card>

          <Card title="Publishing" testid="inv-side-publishing">
            <Toggle label="LiftedAtTheMoment Store"
                    checked={(p.publishing_channels || {storefront:true}).storefront !== false}
                    onChange={(v) => update({ publishing_channels: { ...(p.publishing_channels || {}), storefront: v }})}
                    testid="inv-pub-store" />
            <Toggle label="Lifted Supplies Same-Day"
                    checked={(p.publishing_channels || {}).supplies === true || !!p.same_day_eligible}
                    onChange={(v) => update({ publishing_channels: { ...(p.publishing_channels || {}), supplies: v }})}
                    testid="inv-pub-supplies" />
            <Toggle label="Affiliate Catalog"
                    checked={(p.publishing_channels || {affiliate:true}).affiliate !== false}
                    onChange={(v) => update({ publishing_channels: { ...(p.publishing_channels || {}), affiliate: v }})}
                    testid="inv-pub-affiliate" />
          </Card>

          {/* Iter85 — Square integration card. Toggle controls whether
              the product is pushed to the live Square catalogue on every
              save. The "Refresh Square Inventory" button forces an
              immediate one-shot sync regardless of the toggle. */}
          <Card title="Square Inventory" testid="inv-side-square">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="text-lifted-dim">Status</span>
              <SquareStatusBadge product={p} />
            </div>
            <Toggle
              label="Available on Square"
              checked={p.available_on_square !== false}
              onChange={(v) => update({ available_on_square: v })}
              testid="inv-square-available-toggle"
            />
            <p className="text-[0.65rem] text-lifted-dim mb-2">
              {p.owner_type === "partner" || p.owner_type === "franchise" || p.owner_type === "franchisee"
                ? "Partner / franchise products only sync once an admin approves them."
                : "Lifted Core products sync automatically when this is ON."}
            </p>
            {!isNew && (
              <button
                type="button"
                onClick={() => refreshSquare()}
                disabled={squareBusy}
                className="btn-lifted-outline text-xs w-full inline-flex items-center justify-center gap-1 disabled:opacity-50"
                data-testid="inv-square-refresh-btn"
              >
                {squareBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                {squareBusy ? "Syncing…" : "Refresh Square Inventory"}
              </button>
            )}
            {p.square_synced_at && (
              <div className="text-[0.6rem] text-lifted-dim mt-2" data-testid="inv-square-last-synced">
                Last synced: {new Date(p.square_synced_at).toLocaleString()}
              </div>
            )}
            {p.square_sync_error && (
              <div className="text-[0.6rem] text-rose-300 mt-1 break-words"
                   data-testid="inv-square-sync-error">
                Error: {p.square_sync_error}
              </div>
            )}
          </Card>

          <Card title="Pricing" testid="inv-side-pricing">
            <Label>Price</Label>
            <div className="flex items-center gap-1">
              <span className="text-lifted-dim text-sm">$</span>
              <input value={money(p.price_min_cents ?? p.price_cents)}
                     onChange={(e) => {
                       const c = Math.round((parseFloat(e.target.value || "0") || 0) * 100);
                       update({ price_min_cents: c, price_cents: c });
                     }}
                     className="flex-1 bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm"
                     data-testid="inv-price" />
            </div>
            <Label className="mt-2">Compare-at price</Label>
            <div className="flex items-center gap-1">
              <span className="text-lifted-dim text-sm">$</span>
              <input value={money(p.compare_at_price_cents)}
                     onChange={(e) => {
                       const c = Math.round((parseFloat(e.target.value || "0") || 0) * 100);
                       update({ compare_at_price_cents: c });
                     }}
                     className="flex-1 bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm"
                     data-testid="inv-compare-price" />
            </div>
            <Label className="mt-2">Inventory</Label>
            <input type="number" value={p.inventory_count ?? 0}
                   onChange={(e) => update({ inventory_count: parseInt(e.target.value || "0", 10) })}
                   className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm"
                   data-testid="inv-stock" />
          </Card>

          <Card title="Product organization" testid="inv-side-org">
            <Field label="Vendor / source owner" value={p.vendor} onChange={(v) => update({ vendor: v })} testid="inv-vendor" />
            <Field label="Type" value={p.product_type} onChange={(v) => update({ product_type: v })} testid="inv-type" />
            <Label className="mt-2">Source</Label>
            <select value={p.source_type || "imported_legacy"}
                    onChange={(e) => update({ source_type: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm capitalize" data-testid="inv-source-type">
              <option value="admin">Lifted Admin</option>
              <option value="partner">Partner</option>
              <option value="franchisee">Franchise</option>
              <option value="lifted_supplies">Lifted Supplies</option>
              <option value="imported_legacy">Imported (Legacy)</option>
            </select>
          </Card>

          <Card title="Compliance" testid="inv-side-compliance">
            <div className="text-[0.7rem] text-lifted-dim mb-2">
              Status: <span className={`font-bebas tracking-[0.18em] uppercase ${
                p.compliance_status === "compliant"   ? "text-lifted-green"
                : p.compliance_status === "restricted"  ? "text-rose-300"
                : "text-amber-300"
              }`} data-testid="inv-compliance-status">{p.compliance_status || "review_required"}</span>
            </div>
            {(p.compliance?.restricted_states || p.restricted_states || []).length > 0 && (
              <div className="text-[0.7rem] text-lifted-dim mb-2" data-testid="inv-restricted">
                Restricted in: {(p.compliance?.restricted_states || p.restricted_states).join(", ")}
              </div>
            )}
            <button onClick={runCompliance} disabled={isNew}
                    className="btn-ghost text-[0.65rem] w-full inline-flex items-center justify-center gap-1 disabled:opacity-50"
                    data-testid="inv-run-compliance">
              <ShieldCheck className="h-3 w-3" /> Run compliance check
            </button>
          </Card>
        </aside>
      </div>
    </div>
  );
}

// --------------------------- Subcomponents ---------------------------------
function blankProduct() {
  return {
    title: "", description: "", handle: "", vendor: "LiftedAtTheMoment",
    product_type: "", tags: [], images: [], variants: [],
    price_min_cents: 0, price_cents: 0, compare_at_price_cents: 0,
    inventory_count: 0, status: "draft",
    same_day_eligible: false, shipping_available: true,
    source_type: "admin", metafields: {}, publishing_channels: { storefront: true, affiliate: true },
    // Phase 22+ taxonomy — required when creating new products
    owner_type: "lifted", category: "lifted-gear",
    featured: false, is_new: true, is_top_product: false,
  };
}

function Card({ title, testid, children }) {
  return (
    <div className="glass-panel p-4" data-testid={testid}>
      <div className="font-bebas tracking-[0.22em] text-[0.7rem] text-lifted-dim uppercase mb-3">{title}</div>
      {children}
    </div>
  );
}

function Label({ children, className = "" }) {
  return <div className={`font-bebas tracking-[0.18em] text-[0.6rem] text-lifted-dim uppercase mt-2 mb-1 ${className}`}>{children}</div>;
}

function Field({ label, value, onChange, testid, textarea }) {
  return (
    <div className="mb-2">
      <Label>{label}</Label>
      {textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2}
                   className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm"
                   data-testid={testid} />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)}
               className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm"
               data-testid={testid} />
      )}
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-2 py-1.5 cursor-pointer">
      <span className="text-xs text-white">{label}</span>
      <button type="button" onClick={() => onChange(!checked)}
              className={`relative w-9 h-5 rounded-full transition-colors ${checked ? "bg-lifted-green" : "bg-white/15"}`}
              data-testid={testid}>
        <span className={`absolute top-0.5 ${checked ? "right-0.5" : "left-0.5"} h-4 w-4 rounded-full bg-white transition-all`} />
      </button>
    </label>
  );
}

function VariantsCard({ p, update, handle, isNew, setOk, setErr }) {
  const variants = p.variants || [];
  const addVariant = () => update({
    variants: [...variants, { id: `v-${Date.now()}`, title: "", options: {}, price_cents: p.price_min_cents || 0, inventory_count: 0 }],
  });
  const removeVariant = (id) => update({ variants: variants.filter((v) => v.id !== id) });
  const patchVariant = (id, patch) => update({
    variants: variants.map((v) => v.id === id ? { ...v, ...patch } : v),
  });
  const saveVariants = async () => {
    if (isNew) return;
    try {
      await api.patch(`/admin/inventory/products/${handle}/variants`, { variants });
      setOk("Variants saved.");
    } catch (e) { setErr(apiErrorMessage(e)); }
  };
  return (
    <div className="glass-panel p-4" data-testid="inv-card-variants">
      <div className="flex items-center justify-between mb-3">
        <div className="font-bebas tracking-[0.22em] text-[0.7rem] text-lifted-dim uppercase">Variants</div>
        <div className="flex gap-1">
          <button onClick={addVariant} className="btn-ghost text-[0.65rem] inline-flex items-center gap-1" data-testid="inv-add-variant">
            <Plus className="h-3 w-3" /> Add variant
          </button>
          {!isNew && variants.length > 0 && (
            <button onClick={saveVariants} className="btn-lifted text-[0.65rem]" data-testid="inv-save-variants">
              Save variants
            </button>
          )}
        </div>
      </div>
      {variants.length === 0 ? (
        <p className="text-[0.7rem] text-lifted-dim">No variants. The product is sold as a single SKU.</p>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-[0.55rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
            <tr className="border-b border-white/5">
              <th className="text-left py-1">Variant</th>
              <th className="text-left py-1">Color</th>
              <th className="text-left py-1">Size</th>
              <th className="text-right py-1">Price</th>
              <th className="text-right py-1">Stock</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {variants.map((v) => (
              <tr key={v.id} className="border-b border-white/5">
                <td className="py-1"><input value={v.title || ""} onChange={(e) => patchVariant(v.id, { title: e.target.value })}
                       className="w-full bg-transparent border-b border-white/10 px-1 text-xs"
                       placeholder="Variant name" data-testid={`inv-variant-${v.id}-title`} /></td>
                <td className="py-1"><input value={v.options?.color || ""} onChange={(e) => patchVariant(v.id, { options: { ...v.options, color: e.target.value }})}
                       className="w-24 bg-transparent border-b border-white/10 px-1 text-xs" placeholder="Color" /></td>
                <td className="py-1"><input value={v.options?.size || ""} onChange={(e) => patchVariant(v.id, { options: { ...v.options, size: e.target.value }})}
                       className="w-16 bg-transparent border-b border-white/10 px-1 text-xs" placeholder="Size" /></td>
                <td className="py-1 text-right"><input value={money(v.price_cents)} onChange={(e) => patchVariant(v.id, { price_cents: Math.round((parseFloat(e.target.value||"0")||0) * 100) })}
                       className="w-20 bg-transparent border-b border-white/10 px-1 text-xs text-right" placeholder="0.00" /></td>
                <td className="py-1 text-right"><input type="number" value={v.inventory_count ?? 0} onChange={(e) => patchVariant(v.id, { inventory_count: parseInt(e.target.value||"0", 10) })}
                       className="w-12 bg-transparent border-b border-white/10 px-1 text-xs text-right" /></td>
                <td className="py-1 text-right"><button onClick={() => removeVariant(v.id)} className="text-rose-300 hover:text-rose-200" data-testid={`inv-variant-${v.id}-remove`}>
                  <Trash2 className="h-3 w-3" />
                </button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


// Iter85 — Square status badge. Computes a label from the product's
// stored square_* fields and renders a colour-coded pill.
function SquareStatusBadge({ product }) {
  const p = product || {};
  let label, cls, Icon;
  if (p.available_on_square === false) {
    label = "Off"; cls = "bg-white/10 text-white/50 border-white/20"; Icon = X;
  } else if (p.square_sync_error) {
    label = "Error"; cls = "bg-rose-500/15 text-rose-200 border-rose-400/40"; Icon = AlertCircle;
  } else if (p.square_catalog_id) {
    label = "Synced"; cls = "bg-lifted-green/15 text-lifted-green border-lifted-green/40"; Icon = Check;
  } else {
    label = "Missing"; cls = "bg-amber-400/10 text-amber-200 border-amber-400/40"; Icon = AlertTriangle;
  }
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[0.6rem] uppercase tracking-[0.18em] font-bebas ${cls}`}
      data-testid="inv-square-status-badge"
    >
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}
